<template>
  <div class="guide-errors">
    <h2>Errors and rate limits</h2>
    <p>
      Every API request to eSign Launchpad either succeeds or returns a structured
      error response that explains what went wrong. This page is primarily for
      developers building integrations. It documents the error envelope, the full
      catalog of error codes, and how rate limiting protects the platform from
      accidental abuse.
    </p>
    <p>
      If you are working in the portal directly, you do not interact with API
      errors at all. The portal surfaces friendly, plain-English messages for
      anything that goes wrong. Rate limits also apply in the portal but are
      generous enough that normal human usage never trips them.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      The content below applies to any programmatic caller: REST API clients,
      MCP-tool consumers, and SDK integrations. All non-2xx responses share the
      same envelope, all error codes are stable across versions, and all
      rate-limit responses carry the same standard headers.
    </p>

    <h3>Error response shape</h3>
    <p>All 4xx and 5xx responses use the same envelope:</p>
    <GuideCodeTabs :samples="samples.errorEnvelope" />
    <ul>
      <li><code>errorCode</code>. Stable string you can branch on in code. Codes are documented below and will not change between versions.</li>
      <li><code>message</code>. Human-readable description. Use it for logs and operator-facing UI, not for programmatic decisions.</li>
      <li><code>correlationId</code>. Distributed-trace ID that correlates this response with our server-side logs. Always include it when opening a support ticket.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Error code reference</h3>

    <h3>Authentication &amp; authorization</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Error code</th><th class="text-left">HTTP</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>UNAUTHORIZED</code></td><td>401</td><td>Missing, invalid, or expired bearer token, or the required scope is not granted.</td></tr>
        <tr><td><code>TOKEN_EXPIRED</code></td><td>401</td><td>A signing-ceremony token (for example an embedded signing URL) has expired.</td></tr>
        <tr><td><code>PACKAGE_ACCESS_DENIED</code></td><td>403</td><td>The tenant behind the bearer token does not own this package.</td></tr>
        <tr><td><code>FEATURE_NOT_AVAILABLE</code></td><td>403</td><td>The feature (embedded signing, KBA, etc.) is not enabled on your subscription plan.</td></tr>
        <tr><td><code>PAYMENT_REQUIRED</code></td><td>402</td><td>API access requires the Business tier. Upgrade your subscription to obtain credentials.</td></tr>
      </tbody>
    </q-markup-table>

    <h3>Packages</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Error code</th><th class="text-left">HTTP</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>PACKAGE_NOT_FOUND</code></td><td>404</td><td>The package ID doesn't exist or belongs to another tenant.</td></tr>
        <tr><td><code>PACKAGE_ALREADY_SENT</code></td><td>409</td><td>Attempting a draft-only operation on a package already in Sent or InProgress.</td></tr>
        <tr><td><code>INVALID_PACKAGE_STATE</code></td><td>400</td><td>The package's current status does not permit the requested action. For example, voiding a completed package.</td></tr>
        <tr><td><code>PACKAGE_EXPIRED</code></td><td>400</td><td>The package's expiration date has passed.</td></tr>
        <tr><td><code>PACKAGE_NOT_READY</code></td><td>400</td><td>Send-time validation failed. The response includes a <code>validationErrors</code> array with per-field detail.</td></tr>
      </tbody>
    </q-markup-table>

    <h3>Documents</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Error code</th><th class="text-left">HTTP</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>DOCUMENT_NOT_FOUND</code></td><td>404</td><td>Document not found within the specified package.</td></tr>
        <tr><td><code>DOCUMENT_TOO_LARGE</code></td><td>413</td><td>File exceeds the 25 MB per-file decoded limit.</td></tr>
        <tr><td><code>DOCUMENT_INVALID_TYPE</code></td><td>400</td><td>File type is not PDF, DOC, or DOCX.</td></tr>
        <tr><td><code>DOCUMENT_PROCESSING_FAILED</code></td><td>400</td><td>Asynchronous conversion or text-tag detection failed. Inspect <code>processingError</code> on the document resource.</td></tr>
      </tbody>
    </q-markup-table>

    <h3>Signers, annotations, templates</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Error code</th><th class="text-left">HTTP</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>SIGNER_NOT_FOUND</code></td><td>404</td><td>Signer not found in the specified package.</td></tr>
        <tr><td><code>SIGNER_ALREADY_COMPLETED</code></td><td>409</td><td>Cannot reassign or modify a signer who has already signed.</td></tr>
        <tr><td><code>ANNOTATION_NOT_FOUND</code></td><td>404</td><td>Annotation GUID is not present in the specified package.</td></tr>
        <tr><td><code>TEMPLATE_NOT_FOUND</code></td><td>404</td><td>Template not found or belongs to another tenant.</td></tr>
        <tr><td><code>TEMPLATE_INACTIVE</code></td><td>400</td><td>Template must be active before it can be used to create a package.</td></tr>
      </tbody>
    </q-markup-table>

    <h3>Validation &amp; generic</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Error code</th><th class="text-left">HTTP</th><th class="text-left">Meaning</th></tr>
      </thead>
      <tbody>
        <tr><td><code>VALIDATION_FAILED</code></td><td>400</td><td>Request payload failed shape validation. Typically one or more required fields are missing or malformed.</td></tr>
        <tr><td><code>NOT_FOUND</code></td><td>404</td><td>Generic not-found fallback. Prefer the resource-specific codes above when they apply.</td></tr>
        <tr><td><code>QUOTA_EXCEEDED</code></td><td>429</td><td>Monthly package quota or active-credential limit (25 per tenant) reached.</td></tr>
        <tr><td><code>RATE_LIMITED</code></td><td>429</td><td>Request throttled. See the rate-limit section below for retry guidance.</td></tr>
        <tr><td><code>INTERNAL_ERROR</code></td><td>500</td><td>Unhandled server error. Include the <code>correlationId</code> when reporting.</td></tr>
      </tbody>
    </q-markup-table>

    <q-separator class="q-my-lg" />

    <h3>Rate limits</h3>
    <p>
      Every tenant is subject to a global per-minute velocity cap plus several
      per-endpoint policies that protect expensive operations. When a request is throttled
      the response is <code>429 Too Many Requests</code> with the error code
      <code>RATE_LIMITED</code>.
    </p>

    <h3>Global velocity cap</h3>
    <p>
      The API enforces 120 requests per minute per tenant across all
      <code>/api/v1/*</code> endpoints. The window is a fixed 1-minute slice
      aligned to wall-clock minutes. A burst of 120 requests at 12:00:00 and
      another 120 at 12:00:58 is accepted, but the same pattern at 12:00:59
      pushes some into 12:01.
    </p>

    <h3>Per-endpoint policies</h3>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr>
          <th class="text-left">Policy</th>
          <th class="text-left">Limit</th>
          <th class="text-left">Applies to</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>export</code></td>
          <td>5 req/min</td>
          <td>Void package, cancel scheduled send, certificate download, audit-trail export, audit-integrity verification.</td>
        </tr>
        <tr>
          <td><code>resend</code></td>
          <td>10 req/min</td>
          <td>Resend package notification, resend to a single signer, retry a dead-lettered webhook delivery.</td>
        </tr>
        <tr>
          <td><code>url-import</code></td>
          <td>10 req/min</td>
          <td>Import a document from a public URL.</td>
        </tr>
        <tr>
          <td><code>refund</code></td>
          <td>3 req/min</td>
          <td>Payment refund operations.</td>
        </tr>
        <tr>
          <td>Webhook test</td>
          <td>5 req/min</td>
          <td><code>POST /webhooks/{id}/test</code> synthetic test deliveries.</td>
        </tr>
      </tbody>
    </q-markup-table>

    <h3>Response headers on 429</h3>
    <p>
      Throttled responses include standard rate-limit headers so clients can back off
      precisely:
    </p>
    <GuideCodeTabs :samples="samples.throttledResponse" />
    <ul>
      <li><code>Retry-After</code>. Seconds to wait before retrying. Honor this value before other backoff logic.</li>
      <li><code>RateLimit-Limit</code>. The applicable policy's ceiling (120 for the global cap, or the per-policy limit above).</li>
      <li><code>RateLimit-Remaining</code>. Remaining requests in the current window.</li>
      <li><code>RateLimit-Reset</code>. Unix timestamp (in seconds) when the window resets.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Client retry strategy</h3>
    <p>A production-quality integration should:</p>
    <ol>
      <li>Retry only on 429, 502, 503, or 504. Do not retry on 4xx errors that indicate bad input.</li>
      <li>On 429, sleep for <code>Retry-After</code> seconds before retrying.</li>
      <li>On 5xx, use exponential backoff with jitter (for example 1s, 2s, 4s, 8s, plus random 0 to 500 ms).</li>
      <li>Stop after three to five attempts and surface the failure to your caller with the correlation ID.</li>
      <li>Never retry non-idempotent operations (<code>POST /packages</code>, <code>POST /send</code>) in a tight loop. You can accidentally send the same package twice.</li>
    </ol>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      <strong>Idempotency:</strong> The API does not currently accept an
      idempotency-key header. Use the response from the first attempt of a write
      operation to determine whether a retry is safe. If you got a
      <code>201 Created</code> with a package GUID, the package exists, and
      another retry would create a duplicate.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h3>Related</h3>
    <ul>
      <li><a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>. How the bearer token is validated and how scopes gate operations.</li>
      <li><a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a>. Prefer webhooks over polling to stay well under the rate cap.</li>
    </ul>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const samples = {
    errorEnvelope: {
        json: `{
  "errorCode": "PACKAGE_NOT_FOUND",
  "message": "Package with ID 3f8b... not found",
  "correlationId": "00-7c9a1b2e...-01"
}`
    },
    throttledResponse: {
        http: `HTTP/1.1 429 Too Many Requests
Retry-After: 12
RateLimit-Limit: 120
RateLimit-Remaining: 0
RateLimit-Reset: 1744579260
Content-Type: application/json

{
  "errorCode": "RATE_LIMITED",
  "message": "Request rate limit exceeded.",
  "correlationId": "00-..."
}`
    }
};

export default {
    name: 'GuideErrors',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
